// Загрузка данных экрана: состояние, ошибка, повтор. Ответ помнит, для каких
// зависимостей он получен: устаревший (экран ушёл, зависимости сменились)
// отбрасывается, а «загружается» — это «для текущих зависимостей ответа ещё нет».
import { useCallback, useEffect, useState, type DependencyList } from 'react';

import { ApiError } from '@/api/client';

import { useScreenActivity } from './screenMemory';

export interface AsyncState<T> {
  data: T | null;
  error: string | null;
  loading: boolean;
  reload: () => void;
  setData: (data: T) => void;
}

export const OFFLINE_TEXT = 'Нет связи с сервером. Проверьте интернет и попробуйте ещё раз.';

export function errorText(err: unknown, fallback = 'Не удалось загрузить данные'): string {
  if (err instanceof ApiError) return err.message;
  // fetch без сети падает TypeError, а не ответом сервера: «не удалось загрузить»
  // тут не объясняет, что делать.
  if (err instanceof TypeError) return OFFLINE_TEXT;
  return fallback;
}

interface Settled<T> {
  key: readonly unknown[];
  data: T | null;
  error: string | null;
}

function sameKey(a: readonly unknown[], b: readonly unknown[]): boolean {
  return a.length === b.length && a.every((item, index) => Object.is(item, b[index]));
}

export function useAsync<T>(load: () => Promise<T>, deps: DependencyList): AsyncState<T> {
  const [attempt, setAttempt] = useState(0);
  // Ключ запроса: зависимости вызывающего и номер повтора.
  const key = [...deps, attempt];
  const [settled, setSettled] = useState<Settled<T> | null>(null);

  useEffect(() => {
    let cancelled = false;
    load().then(
      (data) => {
        if (!cancelled) setSettled({ key, data, error: null });
      },
      (err: unknown) => {
        if (!cancelled) setSettled({ key, data: null, error: errorText(err) });
      },
    );
    return () => {
      cancelled = true;
    };
    // load и key пересоздаются на каждый рендер; перезапуск задают их элементы.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, key);

  // Вернулись на экран из стека: данные мог поменять экран выше. Обновляются
  // тихо, без «загружается», а ошибка не стирает показанное.
  const { returns } = useScreenActivity();
  useEffect(() => {
    if (returns === 0) return;
    let cancelled = false;
    load().then(
      (data) => {
        if (!cancelled)
          setSettled((prev) =>
            prev && sameKey(prev.key, key) ? { key, data, error: null } : prev,
          );
      },
      () => undefined,
    );
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [returns]);

  const reload = useCallback(() => setAttempt((n) => n + 1), []);
  const setData = (data: T) => setSettled({ key, data, error: null });
  const current = settled && sameKey(settled.key, key) ? settled : null;
  return {
    data: current?.data ?? null,
    error: current?.error ?? null,
    loading: current === null,
    reload,
    setData,
  };
}
